import React, { useState, useEffect } from 'react';
import './ClientList.css';
import '../../styles/DataGrid.css';
import Card from '../common/Card';
import Table from '../common/Table';
import Pagination from '../common/Pagination';
import { auth } from '../../services/api';

interface Client {
  id: number;
  client_name: string;
  client_acronym?: string;
  client_type: string;
  client_website?: string;
  active: boolean;
  property_count?: number;
  account_count?: number;
  // Primary contact info from backend
  primary_contact?: string;
  email?: string;
  phone?: string;
  // Primary address info from backend
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface Property {
  id: number;
  property_name: string;
  client_id: number;
  client_name: string;
}

interface ClientListProps {
  onClientSelect: (clientId: number) => void;
  onAddClient: () => void;
}

const ClientList: React.FC<ClientListProps> = ({ onClientSelect, onAddClient }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{type: 'client', item: any}>>([]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/clients`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/properties`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchProperties();
  }, []);

  // Autocomplete search
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    if (value.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const lowerValue = value.toLowerCase();
    const matchingClients = clients.filter(client => {
      return client.client_name.toLowerCase().includes(lowerValue)
    }).slice(0, 5).map(c => ({ type: 'client' as const, item: c }));

    setSuggestions(matchingClients);
    setShowSuggestions(suggestions.length > 0);
  };

  const handleSuggestionClick = (suggestion: {type: 'client', item: any}) => {
    setSearchTerm(suggestion.item.client_name);
    setShowSuggestions(false);
  };

  const filteredClients = clients.filter(client => {
    const lowerSearch = searchTerm.toLowerCase();
    return client.client_name.toLowerCase().includes(lowerSearch);
  });

  const totalPages = Math.ceil(filteredClients.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + rowsPerPage);

  const columns = [
    {
      key: 'client',
      title: 'Client',
      width: '25%'
    },
    {
      key: 'contact',
      title: 'Contact Info',
      width: '25%'
    },
    {
      key: 'location',
      title: 'Location',
      width: '20%'
    },
    {
      key: 'stats',
      title: 'Properties / Accounts',
      width: '15%',
      align: 'center' as const
    },
    {
      key: 'status',
      title: 'Status',
      width: '10%',
      align: 'center' as const
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '5%',
      align: 'center' as const
    }
  ];

  const transformedData = paginatedClients.map(client => ({
    client: (
      <div className="client-info">
        <div className="client-name" onClick={() => onClientSelect(client.id)}>
          {client.client_name}
        </div>
        <div className="client-type">{client.client_type}</div>
      </div>
    ),
    contact: (
      <div className="contact-info">
        <div className="contact-name">{client.primary_contact || '-'}</div>
        <div className="contact-email">{client.email || '-'}</div>
        <div className="contact-phone">{client.phone || '-'}</div>
      </div>
    ),
    location: (
      <div className="location-info">
        <div>{client.address || '-'}</div>
        <div>{client.city || '-'}, {client.state || '-'} {client.zip_code || '-'}</div>
      </div>
    ),
    stats: (
      <div className="stats-info-inline">
        <span className="stat-pill properties">{client.property_count || 0} Properties</span>
        <span className="stat-pill accounts">{client.account_count || 0} Accounts</span>
      </div>
    ),
    status: (
      <div className="status-toggle">
        <div className={`simple-toggle ${client.active ? 'on' : 'off'}`}>
          <div className="toggle-slider"></div>
        </div>
      </div>
    ),
    actions: (
      <button
        className="data-grid-action-btn"
        onClick={() => onClientSelect(client.id)}
        title="View Details"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </button>
    )
  }));

  return (
    <div className="client-list">
      <div className="page-header">
        <h1>Client Management</h1>
        <button
          className="btn-primary"
          onClick={onAddClient}
        >
          + Add Client
        </button>
      </div>

      <Card className="data-grid-table">
        <div className="data-grid-navigation">
          <div className="data-grid-search">
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by client name..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => searchTerm.length > 0 && handleSearchChange(searchTerm)}
                className="search-input"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions">
                  {suggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <>
                        <div className="suggestion-name">{suggestion.item.client_name}</div>
                        <div className="suggestion-detail">{suggestion.item.address || (suggestion.item.city ? `${suggestion.item.city}, ${suggestion.item.state}` : '-')}</div>
                      </>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="data-grid-rows-control">
            <span>Show</span>
            <select 
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={10}>10 rows</option>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
            </select>
          </div>
        </div>

        <div className="data-grid-body">
          {loading ? (
            <div className="loading">Loading clients...</div>
          ) : filteredClients.length > 0 ? (
            <>
              <Table
                columns={columns}
                data={transformedData}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                totalItems={filteredClients.length}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          ) : (
            <div className="empty-state">
              <p>No clients found</p>
            </div>
          )}
        </div>
      </Card>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Client</h2>
            <p>Client creation form coming soon...</p>
            <button onClick={() => setShowAddModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;